import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faCheck,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import axios from "axios";
import SEO from "../components/SEO";
import ContactBanner from "../assets/contact.webp"

const ContactPage = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    captcha: ""
  });

  const [formErrors, setFormErrors] = useState({});
  const [captchaText, setCaptchaText] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const formRef = useRef(null);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  // Generate random captcha text
  const generateCaptcha = () => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let captcha = '';
    for (let i = 0; i < 6; i++) {
      captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return captcha;
  };
  
  // Draw captcha on canvas
  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Add noise (dots)
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
    
    // Add lines for more complexity
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    
    // Draw text with slight rotation for each character
    let x = canvas.width / (text.length + 1);
    for (let i = 0; i < text.length; i++) {
      ctx.save();
      ctx.translate(x * (i + 1), canvas.height / 2);
      ctx.rotate((Math.random() - 0.5) * 0.4);
      ctx.fillStyle = `rgb(${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)})`;
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
  };
  
  // Initialize captcha on component mount
  useEffect(() => {
    refreshCaptcha();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Refresh captcha
  const refreshCaptcha = () => {
    const newCaptcha = generateCaptcha();
    setCaptchaText(newCaptcha);
    drawCaptcha(newCaptcha);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.subject.trim()) {
      errors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Validate captcha
    if (formData.captcha.trim() === '') {
      setFormError('Please enter the captcha text');
      return;
    }
    
    if (formData.captcha !== captchaText) {
      setFormError('Captcha verification failed. Please try again.');
      refreshCaptcha();
      setFormData({
        ...formData,
        captcha: ''
      });
      return;
    }
    
    // Reset error if validation passes
    setFormError('');
    setIsSubmitting(true);
    
    try {
      const response = await axios.post("https://zenitechsol.com/contact.php", formData);

      if (response.data === 'success') {
        setFormSuccess('We have recieved your query. We\'ll get back to you soon!');
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          captcha: ""
        });
        refreshCaptcha();
        
        // Reset form status after 5 seconds
        setTimeout(() => {
          setFormSuccess('');
        }, 8000);
      } else if (response.data === 'failed') {
        setFormError('Failed to send your message. Please try again later.');
      }
    }
    catch (error) {
      console.error('Email sending failed:', error);
      setFormError('Failed to send your message. Please try again later.');
    }
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact Us" 
        description="Learn about our company's journey, mission, vision, and core values. From humble beginnings to industry leadership, discover the story behind our success."
        keywords="company history, mission, vision, core values, digital transformation, innovation"
      />
      {/* Hero Section */}
      <ContactHero img={ContactBanner}>
        <Container>
          <HeroContent>
            <HeroTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Get In <span>Touch</span> With Us
            </HeroTitle>
            <HeroSubtitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              We'd love to hear from you. Whether you have a question about our
              services, pricing, or anything else, our team is ready to answer
              all your questions.
            </HeroSubtitle>
          </HeroContent>
        </Container>
      </ContactHero>

      {/* Contact Info Section */}
      <ContactInfoSection>
        <Container>
          <InfoBoxesWrapper>

          <InfoBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <InfoIcon>
                <FontAwesomeIcon icon={faEnvelope} />
              </InfoIcon>
              <InfoTitle>Email Us</InfoTitle>
              <InfoText>
                <ContactLink href="mailto:info@zenitechsol.com">
                  info@zenitechsol.com
                </ContactLink>{" "}
                <br />
                <ContactLink href="mailto:support@zenitechsol.com">
                  support@zenitechsol.com
                </ContactLink>
              </InfoText>
            </InfoBox>

            <InfoBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <InfoIcon>
                <FontAwesomeIcon icon={faFacebookF} />
              </InfoIcon>
              <InfoTitle>Facebook</InfoTitle>
              <InfoText>
                <ContactLink href="https://facebook.com/zenitechsol" target="_blank" rel="noopener noreferrer">
                  facebook.com/zenitechsol
                </ContactLink>
              </InfoText>
            </InfoBox>

            <InfoBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <InfoIcon>
                <FontAwesomeIcon icon={faInstagram} />
              </InfoIcon>
              <InfoTitle>Instagram</InfoTitle>
              <InfoText>
                <ContactLink href="https://instagram.com/zenitechsol" target="_blank" rel="noopener noreferrer">
                  instagram.com/zenitechsol
                </ContactLink>
              </InfoText>
            </InfoBox>

            <InfoBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <InfoIcon>
                <FontAwesomeIcon icon={faLinkedinIn} />
              </InfoIcon>
              <InfoTitle>LinkedIn</InfoTitle>
              <InfoText>
                <ContactLink href="https://linkedin.com/company/zenitechsol" target="_blank" rel="noopener noreferrer">
                  linkedin.com/company/zenitechsol
                </ContactLink>
              </InfoText>
            </InfoBox>
          </InfoBoxesWrapper>
        </Container>
      </ContactInfoSection>

      {/* Contact Form & Map Section */}
      <ContactFormSection>
        <Container>
          <ContactWrapper>
            <ContactFormContainer
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FormTitle>Send Us A Message</FormTitle>
              <FormDescription>
                Fill out the form below, and we'll get back to you as soon as
                possible.
              </FormDescription>

              <ContactForm
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                onSubmit={handleSubmit}
                ref={formRef}
              >
                {formError && <ErrorMessage>{formError}</ErrorMessage>}
                {formSuccess && <SuccessMessage><FontAwesomeIcon icon={faCheck} /> {formSuccess}</SuccessMessage>}

                <FormGroup>
                  <FormLabel>First Name</FormLabel>
                  <FormInput
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  {formErrors.firstName && <ErrorMessage>{formErrors.firstName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Last Name</FormLabel>
                  <FormInput
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                  {formErrors.lastName && <ErrorMessage>{formErrors.lastName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Your Email</FormLabel>
                  <FormInput
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Subject</FormLabel>
                  <FormInput
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                  {formErrors.subject && <ErrorMessage>{formErrors.subject}</ErrorMessage>}
                </FormGroup>

                <FormGroup className="full-width">
                  <FormLabel>Your Message</FormLabel>
                  <FormTextarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                  {formErrors.message && <ErrorMessage>{formErrors.message}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup className="captcha-group">
                  <FormLabel>Verification</FormLabel>
                  <CaptchaContainer>
                    <CaptchaCanvas ref={canvasRef} width="200" height="70" />
                    <RefreshButton type="button" onClick={refreshCaptcha}>
                      <FontAwesomeIcon icon={faSync} />
                    </RefreshButton>
                  </CaptchaContainer>
                  <CaptchaInput
                    type="text"
                    name="captcha"
                    placeholder="Enter captcha text"
                    value={formData.captcha}
                    onChange={handleChange}
                    required
                  />
                  {formErrors.captcha && <ErrorMessage>{formErrors.captcha}</ErrorMessage>}
                </FormGroup>

                <FormGroup className="full-width">
                  <SubmitButton
                    type="submit"
                    name="contact"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </SubmitButton>
                </FormGroup>
              </ContactForm>
            </ContactFormContainer>

            {/* <MapContainer
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <MapTitle>Our Location</MapTitle>
              <MapFrame>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3168.639290621062!2d-122.08624708469227!3d37.42199997982362!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fba02425dad8f%3A0x29cdf01a44fc687f!2sGoogle%20Building%2040%2C%20Mountain%20View%2C%20CA%2094043%2C%20USA!5e0!3m2!1sen!2sus!4v1625764215722!5m2!1sen!2sus" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Office Location Map"
                ></iframe>
              </MapFrame>
            </MapContainer> */}
          </ContactWrapper>
        </Container>
      </ContactFormSection>

      {/* FAQ Section */}
      <FAQSection>
        <Container>
          <SectionTitle>
            <SubTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              FREQUENTLY ASKED QUESTIONS
            </SubTitle>
            <Title
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Have Any <span>Questions?</span>
            </Title>
            <Description
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Find answers to the most common questions about our services and
              processes.
            </Description>
          </SectionTitle>

          <FAQWrapper>
            {faqData.map((faq, index) => (
              <FAQItem
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQWrapper>
        </Container>
      </FAQSection>
    </>
  );
};

// Styled Components
const ContactHero = styled.section`
  height: 50vh;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${({ img }) => img});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 70px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  z-index: 1;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 48px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.2;

  span {
    color: #ff6e6e;
    position: relative;
    display: inline-block;

    &:after {
      content: "";
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(255, 94, 20, 0.3);
      z-index: -1;
    }
  }

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0;
  line-height: 1.8;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContactInfoSection = styled.section`
  padding: 80px 0;
  background-color: #fff;
`;

const InfoBoxesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 30px;
`;

const InfoBox = styled(motion.div)`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const InfoIcon = styled.div`
  width: 70px;
  height: 70px;
  background-color: rgba(255, 94, 20, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #ff6e6e;
  font-size: 28px;
`;

const InfoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const InfoText = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.8;
  margin: 0;
`;

const ContactLink = styled.a`
  color: #666;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6e6e;
  }
`;

const ContactFormSection = styled.section`
  padding: 80px 0;
  background-color: #f9f9f9;
`;

const ContactWrapper = styled.div`
  display: flex;
  gap: 30px;
`;

const ContactFormContainer = styled(motion.div)`
  background-color: #fff;
  border-radius: 8px;
  padding: 40px;
  width: 100%;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const FormDescription = styled.p`
  font-size: 15px;
  color: #666;
  margin-bottom: 30px;
`;

const ContactForm = styled(motion.form)`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  &.full-width {
    grid-column: 1 / -1;
  }
  
  &.captcha-group {
    grid-column: 1 / -1;
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  text-align: left;
`;

const FormInput = styled.input`
  width: 94%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6e6e;
    box-shadow: 0 0 0 2px rgba(255, 94, 20, 0.2);
  }

  &.error {
    border-color: #ff3b30;
  }
`;

const FormTextarea = styled.textarea`
  width: 96%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6e6e;
    box-shadow: 0 0 0 2px rgba(255, 94, 20, 0.2);
  }

  &.error {
    border-color: #ff3b30;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  grid-column: 1 / -1;

  svg {
    margin-right: 10px;
  }
`;

const ErrorMessage = styled.p`
  color: #ff0000;
  font-size: 14px;
  margin-top: 5px;
  margin-bottom: 0;
`;

const CaptchaCanvas = styled.canvas`
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-right: 10px;
`;

const CaptchaContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const RefreshButton = styled.button`
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const SubmitButton = styled(motion.button)`
  display: inline-block;
  padding: 14px 30px;
  background-color: #ff6e6e;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #e04d0d;
  }
`;

const FAQSection = styled.section`
  padding: 80px 0;
  background-color: #fff;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;

  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    width: 30px;
    height: 1px;
    background-color: #ff6e6e;
  }

  &:before {
    left: -40px;
  }

  &:after {
    right: -40px;
  }
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;

  span {
    color: #ff6e6e;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FAQWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 25px 30px;
  margin-bottom: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FAQQuestion = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const FAQAnswer = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.8;
  margin: 0;
`;

// FAQ Data
const faqData = [
  {
    id: 1,
    question: "What services do you offer?",
    answer:
      "We offer a comprehensive range of digital services including web design and development, mobile app development, UI/UX design, digital marketing, branding, and e-commerce solutions. Our team specializes in creating custom solutions tailored to your specific business needs.",
  },
  {
    id: 2,
    question: "How long does it take to complete a project?",
    answer:
      "Project timelines vary depending on the scope and complexity. A simple website might take 2-4 weeks, while a complex web application could take 2-6 months. During our initial consultation, we'll provide you with a detailed timeline based on your specific requirements.",
  },
  {
    id: 3,
    question: "What is your pricing structure?",
    answer:
      "We offer flexible pricing options including project-based quotes, hourly rates, and retainer packages. Each project is unique, so we provide custom quotes based on your specific needs, timeline, and budget constraints. Contact us for a free consultation and quote.",
  },
  {
    id: 4,
    question: "Do you offer maintenance and support after project completion?",
    answer:
      "Yes, we offer various maintenance and support packages to ensure your digital assets remain secure, up-to-date, and performing optimally. Our support packages include regular updates, security monitoring, performance optimization, and technical support.",
  },
  {
    id: 5,
    question: "How do we get started working together?",
    answer:
      "Getting started is easy! Simply contact us through our contact form, email, or phone. We'll schedule an initial consultation to discuss your project requirements, goals, timeline, and budget. After gathering all necessary information, we'll provide you with a detailed proposal and once approved, we'll begin the development process.",
  },
];

export default ContactPage;

const CaptchaInput = styled(FormInput)`
  width: 200px;
  margin-right: auto;
  display: flex;
`;
